// Centralized fetch wrapper for LiveSphere front-ends.
//
// Goals:
// - Standardize base URL handling across environments
// - Ensure cookies (auth/session) are included by default
// - Provide a single place to add cross-cutting concerns (logging, retry, tracing)
// - Avoid changing existing endpoint contracts/payloads
//
// Usage:
//   import { apiFetch, apiFetchJson, ApiError } from "../api/apiFetch";
//   const res = await apiFetch("account/user?appid=123");
//   const data = await apiFetchJson("photo/getlibraries?appid=123");
//
// Notes:
// - We default to `credentials: "include"` because this app relies on cookie-based auth.
// - If you ever move to pure bearer tokens, this is still the right place to attach them.
import { GET_ENDPOINTS_REQUIRING_USERID } from "./apiRequirements";
import { ENDPOINT_METHODS } from "./apiEndpointMethods";

export class ApiError extends Error {
  constructor(message, { status, url, bodyText } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.bodyText = bodyText;
  }
}

// Some legacy call sites used GET with a JSON body (common in the old BFF layer).
// Browsers either drop GET bodies or treat them inconsistently, which results in
// missing parameters (often surfacing as 400s/405s). While we modernize endpoint
// design later, we normalize GET+JSON-body into a querystring.
function tryPromoteJsonBodyToQuery(url, options) {
  const method = (options.method || "GET").toUpperCase();
  if (method !== "GET") return { url, options };

  const body = options.body;
  if (!body || typeof body !== "string") return { url, options };

  const headers = options.headers || {};
  const contentType = (headers["Content-Type"] || headers["content-type"] || "").toLowerCase();
  if (!contentType.includes("application/json")) return { url, options };

  let obj;
  try {
    obj = JSON.parse(body);
  } catch {
    return { url, options };
  }

  // Only support flat objects (Index, Count, etc.)
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return { url, options };

  const absolute = isAbsoluteUrl(url);
  const u = new URL(url, absolute ? undefined : "http://placeholder");
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    u.searchParams.set(String(k).toLowerCase(), String(v));
  });

  const newOptions = { ...options };
  delete newOptions.body;

  // Remove content-type header since there's no body anymore.
  const newHeaders = { ...(headers || {}) };
  delete newHeaders["Content-Type"];
  delete newHeaders["content-type"];
  newOptions.headers = newHeaders;

  return { url: absolute ? u.toString() : (u.pathname + u.search), options: newOptions };
}

function getStoredUserId() {
  try {
    const userText = localStorage.getItem("user");
    if (!userText) return "";
    const user = JSON.parse(userText);
    return (user && (user.userID || user.userid || user.UserID)) ? String(user.userID || user.userid || user.UserID).trim() : "";
  } catch {
    return "";
  }
}

function normalizeEndpointKeyFromUrl(url) {
  try {
    const u = new URL(url, isAbsoluteUrl(url) ? undefined : "http://placeholder");
    let p = (u.pathname || "").toLowerCase();
    // Strip API prefixes so we end up with "controller/action".
    if (p.startsWith("/api/v1/")) p = p.slice("/api/v1/".length);
    else if (p.startsWith("/api/")) p = p.slice("/api/".length);
    else if (p.startsWith("/")) p = p.slice(1);
    return p;
  } catch {
    return "";
  }
}

function ensureUserIdQueryParam(url, options) {
  const key = normalizeEndpointKeyFromUrl(url);
  if (!key || !GET_ENDPOINTS_REQUIRING_USERID.has(key)) return { url, options };

  const userId = (options && (options.userId || options.userid)) || getStoredUserId();
  if (!userId) return { url, options };

  const absolute = isAbsoluteUrl(url);
  const u = new URL(url, absolute ? undefined : "http://placeholder");
  // If any casing variant exists, don't override it.
  const hasUserId = [...u.searchParams.keys()].some(k => k.toLowerCase() === "userid");
  if (!hasUserId) u.searchParams.set("userid", userId);

  const newOptions = { ...options };
  delete newOptions.userId;
  delete newOptions.userid;

  return { url: absolute ? u.toString() : (u.pathname + u.search), options: newOptions };
}

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url);
}

function joinUrl(base, path) {
  if (!base) return path;
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  let p = path.startsWith("/") ? path : `/${path}`;

  // Guardrail: older call sites sometimes include the API prefix already
  // (e.g. "/api/posts/getposts"), which would become "/api/v1/api/posts/getposts"
  // if our base is "/api/v1".
  const bl = b.toLowerCase();
  const pl = p.toLowerCase();

  if (bl.endsWith("/api/v1")) {
    if (pl === "/api/v1") p = "";
    else if (pl.startsWith("/api/v1/")) p = p.slice("/api/v1".length);
    else if (pl === "/api") p = "";
    else if (pl.startsWith("/api/")) p = p.slice("/api".length);
  } else if (bl.endsWith("/api")) {
    if (pl === "/api") p = "";
    else if (pl.startsWith("/api/")) p = p.slice("/api".length);
  }

  return `${b}${p}`;
}

export function getApiBaseUrl() {
  // CRA environment variable convention:
  // - put REACT_APP_API_BASE_URL in ClientApp/.env or in your k8s deployment env vars.
  // - leave unset to use relative URLs (same origin).
  const raw = (process.env.REACT_APP_API_BASE_URL || "").trim();

  // Default to v1 on the same origin.
  if (!raw) return "/api/v1";

  // Convenience: if someone sets REACT_APP_API_BASE_URL=https://host/api
  // automatically target the v1 surface.
  const noTrailingSlash = raw.endsWith("/") ? raw.slice(0, -1) : raw;
  if (noTrailingSlash.toLowerCase().endsWith("/api")) {
    return `${noTrailingSlash}/v1`;
  }

  return raw;
}

function getStoredAccessToken() {
  try {
    const userText = localStorage.getItem("user");
    if (!userText) return "";
    const user = JSON.parse(userText);
    return (user && user.accessToken) ? String(user.accessToken).trim() : "";
  } catch {
    return "";
  }
}

export async function apiFetch(url, options = {}) {
    const base = getApiBaseUrl();

    // First, build the URL (relative -> base) so the helper can safely
    // convert GET+JSON-body into query params.
    const prelimUrl = isAbsoluteUrl(url) ? url : joinUrl(base, url);

    // If a call site uses the wrong verb (common after removing the BFF),
    // attempt a safe correction. We only auto-correct POST -> GET when the API
    // endpoint only supports GET.
    const normalizedOptions = { ...options };
    try {
      const absolute = isAbsoluteUrl(prelimUrl);
      const u = new URL(prelimUrl, absolute ? undefined : "http://placeholder");
      let p = (u.pathname || "").toLowerCase().replace(/^\//, "");
      if (p.startsWith("api/v1/")) p = p.slice("api/v1/".length);
      else if (p.startsWith("api/")) p = p.slice("api/".length);

      // Direct match first, otherwise try suffix match (handles some controllers
      // that use nested route prefixes).
      let allowed = ENDPOINT_METHODS[p];
      if (!allowed) {
        const keys = Object.keys(ENDPOINT_METHODS);
        const hit = keys.find(k => k === p || k.endsWith("/" + p));
        if (hit) allowed = ENDPOINT_METHODS[hit];
      }

      const requested = (normalizedOptions.method || "GET").toUpperCase();
      if (allowed && requested === "POST" && !allowed.includes("POST") && allowed.includes("GET")) {
        normalizedOptions.method = "GET";
      }
    } catch {
      // ignore
    }

    // Normalize legacy GET bodies into query params.
    const promoted = tryPromoteJsonBodyToQuery(prelimUrl, normalizedOptions);
    let finalUrl = promoted.url;

    const token = promoted.options.token || options.token || getStoredAccessToken();
    const userId = promoted.options.userId || options.userId || getStoredUserId();

    // Auto-inject userid for endpoints that require it (many old BFF services
    // added userid server-side; now we call the API directly).
    try {
      const absolute = isAbsoluteUrl(finalUrl);
      const u = new URL(finalUrl, absolute ? undefined : "http://placeholder");

      // Compute a comparable relative path like "photo/getlibraries".
      let p = (u.pathname || "").toLowerCase();
      p = p.replace(/^\//, "");
      if (p.startsWith("api/v1/")) p = p.slice("api/v1/".length);
      else if (p.startsWith("api/")) p = p.slice("api/".length);

      const hasUserId = Array.from(u.searchParams.keys()).some(k => k.toLowerCase() === "userid");
      if (!hasUserId && userId && GET_ENDPOINTS_REQUIRING_USERID.has(p)) {
        u.searchParams.set("userid", userId);
        finalUrl = absolute ? u.toString() : (u.pathname + "?" + u.searchParams.toString());
      }
    } catch {
      // Non-fatal: if URL parsing fails, just proceed.
    }

    const mergedOptions = {
        // credentials: "include", // enable if you move back to cookie auth
        ...promoted.options,
        headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(promoted.options.headers || {}),
        },
    };

    delete mergedOptions.token;
    delete mergedOptions.userId;

    return fetch(finalUrl, mergedOptions);
}


export async function apiFetchJson(url, options = {}) {
  const res = await apiFetch(url, options);

  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = await res.text();
    } catch {
      bodyText = "";
    }
    throw new ApiError(`Request failed: ${res.status}`, {
      status: res.status,
      url,
      bodyText,
    });
  }

  // Some endpoints return empty bodies (204, etc.)
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // If server returns non-JSON unexpectedly, still surface it.
    throw new ApiError("Invalid JSON in response", {
      status: res.status,
      url,
      bodyText: text,
    });
  }
}
